import { Typography, Box } from '@mui/material'
import ZendeskLink from 'components/Common/ZendeskLink'
import { Field, Form, Formik } from 'formik'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import * as yup from 'yup'
import Button from 'components/Common/Button'

import { AuthStateContext } from 'context/auth'
import { makeSubscribable } from 'helpers'
import TextField from 'components/Common/TextField'

import api from 'api'
import { DISABLED_BUTTON, PRIMARY_BUTTON } from 'themes/theme'

export const emailFieldSchema = yup.string().email('Invalid Email Address').required('Email is required')

const forgotPasswordSchema = yup.object({
  email: emailFieldSchema,
})

const sx = {
  textWrapper: {
    marginBottom: theme => theme.spacing(2),
  },

  titleWrapper: {
    minHeight: theme => theme.spacing(15),
    color: 'var(--primary)',
  },
  inputWrapper: {
    position: 'relative',
    height: 104,
  },
};

const initialValue = {
  email: '',
}

export const ForgotPasswordForm = ({ onNextStep }) => {

  const [, authDispatch] = useContext(AuthStateContext)
  const formik = useRef(null)
  const [forgotPasswordSubscribable, setForgotPasswordSubscribable] = useState(null)

  const onFormSubmit = useCallback(
    values => {
      const promise = api.forgotPassword(values.email.toLowerCase())
      const subscribable = makeSubscribable(promise)
      authDispatch({ type: 'forgotPasswordFormSubmitted', payload: values.email })
      setForgotPasswordSubscribable(subscribable)
    },
    [authDispatch],
  )

  const onForgotPasswordSuccess = useCallback(() => {
    onNextStep()
  }, [onNextStep])

  const onForgotPasswordError = useCallback(error => {
    formik.current?.setSubmitting(false)
    formik.current?.setErrors({ email: error.message })
  }, [])

  useEffect(() => {
    if (forgotPasswordSubscribable) {
      return forgotPasswordSubscribable.subscribe(onForgotPasswordSuccess, onForgotPasswordError)
    }
  }, [onForgotPasswordError, onForgotPasswordSuccess, forgotPasswordSubscribable])

  return (
    <>
      <Formik
        innerRef={formik}
        validationSchema={forgotPasswordSchema}
        initialValues={initialValue}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <Box sx={sx.titleWrapper}>
              <Typography variant="h5" style={{ fontSize: '29px' }}>Recover your password</Typography>
              <Typography component="span" variant="caption" style={{ lineHeight: '20px' }} sx={sx.textWrapper} className={` d-inline-block fw-light mt-3`}>
                Enter your email address and we will send you a link to reset your password. Please refresh your email
                inbox, and check spam and junk folders if you can't find the email.
              </Typography>
            </Box>
            <Box sx={sx.inputWrapper}>
              <Field name="email">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    fullWidth
                    borderLess
                    error={Boolean(meta.touched && meta.error)}
                    helperText={meta.touched && meta.error}
                    disabled={isSubmitting}
                    type="email"
                  />
                )}
              </Field>
            </Box>
            <Box textAlign='center'>
              <Button
                fullWidth
                submit
                type={!isValid ? DISABLED_BUTTON : PRIMARY_BUTTON}
                disabled={!isValid || isSubmitting}
                loading={isSubmitting}
                sx={{ width: '250px' }}
              >
                Send Me Recovery Email
              </Button>
            </Box>
            <ZendeskLink />
          </Form>
        )}
      </Formik>
    </>
  )
}
